import React, { useState } from 'react';
import { ArrowLeft, Calendar, Tag, ChevronRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { newsArticles, NewsArticle } from '../data/news';

type NewsPageProps = {
  onBack: () => void;
};

export const NewsPage: React.FC<NewsPageProps> = ({ onBack }) => {
  const { isDark } = useTheme();
  const [selectedArticle, setSelectedArticle] = useState<NewsArticle | null>(null);

  if (selectedArticle) {
    return (
      <>
        {/* Article Detail View */}
        <section className="relative w-full py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <div className={`w-full h-full ${isDark ? 'bg-vmil-navy' : 'bg-vmil-light'}`} />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <button
              onClick={() => setSelectedArticle(null)}
              className={`inline-flex items-center gap-2 mb-8 transition-colors ${
                isDark ? 'text-white/80 hover:text-white' : 'text-vmil-steel hover:text-vmil-blue'
              }`}
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to News</span>
            </button>

            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-4 mb-6">
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                  isDark ? 'bg-vmil-blue/20 text-vmil-blue' : 'bg-vmil-blue/10 text-vmil-blue'
                }`}>
                  <Tag className="w-4 h-4" />
                  {selectedArticle.category}
                </span>
                <span className={`inline-flex items-center gap-2 text-sm ${
                  isDark ? 'text-white/60' : 'text-vmil-steel'
                }`}>
                  <Calendar className="w-4 h-4" />
                  {selectedArticle.date}
                </span>
              </div>

              <h1 className={`text-3xl md:text-4xl lg:text-5xl font-extrabold mb-8 leading-tight ${
                isDark ? 'text-white' : 'text-vmil-blue'
              }`}>
                {selectedArticle.title}
              </h1>

              <div className={`prose prose-lg max-w-none ${
                isDark ? 'prose-invert' : ''
              }`}>
                {selectedArticle.content.map((paragraph, index) => (
                  <p 
                    key={index} 
                    className={`mb-6 text-base md:text-lg leading-relaxed ${
                      isDark ? 'text-white/80' : 'text-vmil-steel'
                    }`}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      {/* Hero Section */}
      <section className="relative w-full py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/factory-1.jpg" 
            alt="VMIL News" 
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white mb-6">
              News and
              <span className="block text-vmil-blue">Events</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80">
              Stay updated with the latest news and events from Velmurugan Industries.
            </p>
          </div>
        </div>
      </section>

      {/* News List */}
      <section className={`w-full py-16 md:py-24 ${isDark ? 'bg-vmil-navy' : 'bg-white'}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className={`text-2xl md:text-3xl font-bold mb-8 ${
              isDark ? 'text-white' : 'text-vmil-blue'
            }`}>
              Latest News
            </h2>

            <div className="space-y-6">
              {newsArticles.map((article) => (
                <article
                  key={article.id}
                  onClick={() => setSelectedArticle(article)}
                  className={`p-6 md:p-8 rounded-2xl cursor-pointer transition-all hover:shadow-lg ${
                    isDark 
                      ? 'bg-white/5 border border-white/10 hover:border-vmil-blue/30' 
                      : 'bg-vmil-light border border-gray-200 hover:border-vmil-blue/30'
                  }`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs ${
                      isDark ? 'bg-vmil-blue/20 text-vmil-blue' : 'bg-vmil-blue/10 text-vmil-blue'
                    }`}>
                      <Tag className="w-3 h-3" />
                      {article.category}
                    </span>
                    <span className={`inline-flex items-center gap-2 text-xs ${
                      isDark ? 'text-white/60' : 'text-vmil-steel'
                    }`}>
                      <Calendar className="w-3 h-3" />
                      {article.date}
                    </span>
                  </div>

                  <h3 className={`text-xl md:text-2xl font-bold mb-3 ${
                    isDark ? 'text-white' : 'text-vmil-blue'
                  }`}>
                    {article.title}
                  </h3>

                  <p className={`text-sm md:text-base mb-4 line-clamp-3 ${
                    isDark ? 'text-white/70' : 'text-vmil-steel'
                  }`}>
                    {article.content[0]}
                  </p>

                  <span className={`inline-flex items-center gap-1 text-sm font-semibold ${
                    isDark ? 'text-vmil-blue' : 'text-vmil-blue'
                  }`}>
                    Read more
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
